import { state } from '@codesuma/baseline'
import { collection } from './storage'
import type { IOccasion } from './occasions'
import type { ICountryOccasion } from './country-occasions'

// What the reader has done to an occasion that is not theirs. Iran's occasions
// and the ones a country's catalog supplies are read-only lists, so a new name
// or a hiding is kept beside them rather than in them: one small row keyed to
// the occasion, applied when the day is drawn.
//
// The key is the occasion, not the date. A built-in comes back every year, so
// renaming it once renames it every year.
//
// Nothing here hides a day off. The day's list offers hiding only on occasions
// that are not holidays, because isHoliday feeds services/day-holidays, which
// colours the grid, the month list, the year view and the widget — and none of
// those has a row in which to explain a missing one. Being right about Iran's
// days off is the whole job.
//
// A built-in has no id: the lists are [day, month, title] triples. So the key
// carries the occasion's own title, which is the only stable part it has. If a
// title is ever corrected in configs/occasions.ts the key stops matching and the
// occasion comes back under its real name — the safe way for this to fail.

export interface IOccasionOverride {
    id: string
    // The reader's own name for it. Absent when only hidden.
    title?: string
    hidden?: boolean
}

const KEY = 'occasion-overrides'
const repo = collection<IOccasionOverride>(KEY)
let all: IOccasionOverride[] = []

export async function init() {
    all = await repo.load()
}

// Iran's own: the calendar the date is kept in, where in it, and the name. The
// occasion tests assert that none is listed twice on a day, so this is unique.
const builtInKey = (from: string, month: number, day: number, title: string) =>
    `ir:${from}:${month}:${day}:${title}`

// A country's: the country and the occasion's English name, which the catalog
// keeps the same from one year to the next.
const countryKey = (country: string, name: string) => `c:${country}:${name}`

const find = (id: string) => all.find(o => o.id === id)

function broadcast() {
    state.set(KEY, all)
}

function write(row: IOccasionOverride) {
    // Nothing left to say about it, so drop the row rather than keep an empty
    // one that would outlive what it was for.
    if (!row.title && !row.hidden) return clear(row.id)
    all = [...all.filter(o => o.id !== row.id), row]
    void repo.put(row)
    broadcast()
}

// An empty name is not a rename, it is the original name back again.
function rename(id: string, title: string) {
    write({ id, hidden: find(id)?.hidden, title: title.trim() || undefined })
}

// Both ways, so bringing one back keeps the name the reader gave it.
function hide(id: string, on = true) {
    write({ ...find(id), id, hidden: on || undefined })
}

// Everything the reader did to it, undone.
function clear(id: string) {
    if (!find(id)) return
    all = all.filter(o => o.id !== id)
    void repo.remove(id)
    broadcast()
}

// A day's list as the reader has left it: their names in place of the
// originals, and the ones they put away set to one side. In one place, so the
// day page, the month's own list and the widget cannot come to disagree about
// what has been hidden. The keys of the hidden ones come back too, for the
// day page's line that restores them.
//
// services/day-holidays does not come through here, and must not: it asks the
// untouched list whether the day is a day off.
function builtIns(list: IOccasion[]) {
    const shown: (IOccasion & { key: string, original: string })[] = []
    const hidden: string[] = []
    for (const o of list) {
        const key = builtInKey(o.from, o.month, o.day, o.title)
        const kept = find(key)
        if (kept?.hidden) hidden.push(key)
        else shown.push({ ...o, key, original: o.title, title: kept?.title ?? o.title })
    }
    return { shown, hidden }
}

function countries(list: ICountryOccasion[]) {
    const shown: (ICountryOccasion & { key: string, original: string, title: string })[] = []
    const hidden: string[] = []
    for (const o of list) {
        const key = countryKey(o.country, o.names.en)
        const original = o.names.fa ?? o.names.en
        const kept = find(key)
        if (kept?.hidden) hidden.push(key)
        else shown.push({ ...o, key, original, title: kept?.title ?? original })
    }
    return { shown, hidden }
}

export default { init, rename, hide, clear, builtIns, countries }
